import EmptyView from 'general/components/Others/EmptyView';
import AppColors from 'general/constants/AppColors';
import PropTypes from 'prop-types';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import { Table } from 'antd';
import tableStyle from './tableStyle';
import tableStyleAction from './tableStyleAction';
import AppConfigs from 'general/constants/AppConfigs';

const EMPTY_ARRAY = [];
const SELECTED_ROW_BACKGROUND = '#EEF8F2';
const CHECKMARK_ICON = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 10" fill="none"><path d="M1 5L4.5 8.5L11 1.5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>`
);
const INDETERMINATE_ICON = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 10 2" fill="none"><path d="M1 1H9" stroke="white" stroke-width="2" stroke-linecap="round"/></svg>`
);

const SelectableCheckbox = forwardRef(
  ({ checked = false, indeterminate = false, disabled = false, onClick, ...rest }, ref) => {
    const inputRef = useRef(null);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const setRefs = (node) => {
      inputRef.current = node;

      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    const isActive = checked || indeterminate;

    return (
      <input
        {...rest}
        type="checkbox"
        ref={setRefs}
        checked={checked}
        disabled={disabled}
        onClick={onClick}
        onChange={() => {}}
        style={{
          width: 16,
          height: 16,
          minWidth: 16,
          margin: 0,
          cursor: disabled ? 'not-allowed' : 'pointer',
          appearance: 'none',
          WebkitAppearance: 'none',
          borderRadius: 4,
          border: `1px solid ${isActive ? AppColors.primary : AppColors.gray[400]}`,
          backgroundColor: isActive ? AppColors.primary : '#FFFFFF',
          backgroundImage: isActive
            ? `url("data:image/svg+xml,${indeterminate ? INDETERMINATE_ICON : CHECKMARK_ICON}")`
            : 'none',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: indeterminate ? '10px 2px' : '10px 8px',
          opacity: disabled ? 0.6 : 1,
        }}
      />
    );
  }
);

SelectableCheckbox.displayName = 'SelectableCheckbox';

KTTable.propTypes = {
  idType: PropTypes.string,
  columns: PropTypes.array,
  items: PropTypes.array,
  disabledItems: PropTypes.array,
  preselectedItems: PropTypes.array,
  selectableRows: PropTypes.bool,
  striped: PropTypes.bool,
  compactGrid: PropTypes.bool,
  loading: PropTypes.bool,
  onClickedRow: PropTypes.func,
  onDoubleClickedRow: PropTypes.func,
  onSelectedRowsChanged: PropTypes.func,
  clearSelectedRows: PropTypes.bool,
  noDataComponent: PropTypes.element,
  scrollable: PropTypes.bool,
  showBorderBottom: PropTypes.bool,
  hasActionColumn: PropTypes.bool,
  groupHeader: PropTypes.bool,
  showVehicleTypeWhenHideBike: PropTypes.bool,
  selectableRowsNoSelectAll: PropTypes.bool,
};

function KTTable({
  idType = 'id',
  columns = EMPTY_ARRAY,
  items = EMPTY_ARRAY,
  disabledItems = EMPTY_ARRAY,
  preselectedItems = EMPTY_ARRAY,
  selectableRows = true,
  striped = true,
  compactGrid = false,
  loading = false,
  onClickedRow,
  onDoubleClickedRow,
  onSelectedRowsChanged,
  clearSelectedRows = false,
  noDataComponent,
  scrollable = false,
  showBorderBottom = false,
  hasActionColumn = true,
  groupHeader = false,
  showVehicleTypeWhenHideBike = false,
  selectableRowsNoSelectAll = false,
}) {
  const isHideBike = AppConfigs.env.hideBike == 1;
  const [selectedRows, setSelectedRows] = useState(preselectedItems || []);
  const filteredColumns = useMemo(
    () =>
      isHideBike && !showVehicleTypeWhenHideBike
        ? columns.filter((col) => col.id !== 'VEHICLE_TYPE')
        : columns,
    [columns, isHideBike, showVehicleTypeWhenHideBike]
  );
  const preselectedRowIdsKey = useMemo(() => {
    const rows = preselectedItems || [];
    return rows
      .map((item) => item?.[idType])
      .filter((id) => id != null && id !== '')
      .map((id) => String(id))
      .sort()
      .join('|');
  }, [preselectedItems, idType]);

  const disabledRowIdsKey = useMemo(() => {
    const rows = disabledItems || [];
    return rows
      .map((item) => item?.[idType])
      .filter((id) => id != null && id !== '')
      .map((id) => String(id))
      .sort()
      .join('|');
  }, [disabledItems, idType]);

  const selectedIdSet = useMemo(
    () =>
      new Set(
        (selectedRows || [])
          .map((row) => row?.[idType])
          .filter((id) => id != null && id !== '')
          .map((id) => String(id))
      ),
    [selectedRows, idType]
  );

  const resolvedColumns = useMemo(
    () =>
      filteredColumns.map((column) => {
        const isStickyRightColumn =
          column?.style?.position === 'sticky' &&
          [0, '0', '0px'].includes(column?.style?.right);

        if (!isStickyRightColumn) return column;

        return {
          ...column,
          conditionalCellStyles: [
            ...(column?.conditionalCellStyles || []),
            {
              when: (row) => {
                const rowId = row?.[idType];
                return rowId != null && rowId !== '' && selectedIdSet.has(String(rowId));
              },
              style: {
                backgroundColor: SELECTED_ROW_BACKGROUND,
              },
            },
          ],
        };
      }),
    [filteredColumns, selectedIdSet, idType]
  );

  useEffect(() => {
    if (clearSelectedRows) {
      setSelectedRows([]);
      return;
    }
    if (!preselectedRowIdsKey) {
      setSelectedRows([]);
      return;
    }
    const ids = new Set(preselectedRowIdsKey.split('|'));
    const rows = (items || []).filter((row) => {
      const rowId = row?.[idType];
      return rowId != null && rowId !== '' && ids.has(String(rowId));
    });
    setSelectedRows(rows);
  }, [preselectedRowIdsKey, clearSelectedRows, items, idType]);

  useEffect(() => {
    if (!groupHeader) {
      const dataTable = document.querySelector('.rdt_Table');

      if (dataTable) {
        const preventDrag = (event) => {
          event.preventDefault();
        };

        dataTable.addEventListener('dragstart', preventDrag);

        return () => {
          dataTable.removeEventListener('dragstart', preventDrag);
        };
      }
    }
  }, [groupHeader]);

  const disabledIds = useMemo(() => {
    if (!disabledRowIdsKey) return new Set();
    return new Set(disabledRowIdsKey.split('|'));
  }, [disabledRowIdsKey]);

  const preselectedIds = useMemo(() => {
    if (!preselectedRowIdsKey) return new Set();
    return new Set(preselectedRowIdsKey.split('|'));
  }, [preselectedRowIdsKey]);

  const rowDisabledCriteria = useCallback(
    (row) => {
      const rowId = row?.[idType];
      return rowId != null && rowId !== '' && disabledIds.has(String(rowId));
    },
    [disabledIds, idType]
  );

  const rowPreselectedCriteria = useCallback(
    (row) => {
      const rowId = row?.[idType];
      return rowId != null && rowId !== '' && preselectedIds.has(String(rowId));
    },
    [preselectedIds, idType]
  );

  const handleSelectedRowsChanged = useCallback(
    ({ selectedRows }) => {
      setSelectedRows(selectedRows || []);
      if (onSelectedRowsChanged) {
        onSelectedRowsChanged(selectedRows);
      }
    },
    [onSelectedRowsChanged]
  );

  const antRowSelection = selectableRows
    ? {
      selectedRowKeys:
        preselectedItems?.map((item) => item[idType]) || [],

      getCheckboxProps: (record) => ({
        disabled: disabledIds.has(record[idType]),
      }),

      onChange: (selectedRowKeys, selectedRows) => {
        if (onSelectedRowsChanged) {
          onSelectedRowsChanged(selectedRows);
        }
      },
    }
    : null;

  if (groupHeader) {
    return (
      <div
        style={{
          borderRadius: '5px',
          overflow: scrollable ? 'auto' : 'hidden',
        }}
        className={items.length === 0 ? 'no-data-table' : ''}
      >
        <Table
          className={
            items.length === 0
              ? 'no-data-table'
              : 'kt-table-bordered'
          }
          columns={filteredColumns}
          dataSource={items}
          rowSelection={antRowSelection}
          loading={loading}
          locale={{
            emptyText: noDataComponent ?? <EmptyView />,
          }}
          pagination={false}
          scroll={
            scrollable
              ? { x: 'max-content' }
              : undefined
          }
          onRow={(record) => ({
            onClick: () => onClickedRow?.(record),
            onDoubleClick: () =>
              onDoubleClickedRow?.(record),
          })}
          rowKey={idType}
          bordered={showBorderBottom}
          showHeader={items.length > 0}
        />
      </div>
    );
  }

  return (
    <div
      className={`overflow-hidden ${selectableRowsNoSelectAll
        ? 'hide-select-all-checkbox'
        : ''
        }`}
      style={{
        borderLeft:
          items.length > 0
            ? `1px solid ${AppColors.gray[300]}`
            : '',
        borderRight:
          items.length > 0
            ? `1px solid ${AppColors.gray[300]}`
            : '',
        borderRadius: '5px',
      }}
    >
      <DataTable
        keyField={idType}
        columns={resolvedColumns}
        data={items}
        customStyles={
          hasActionColumn
            ? tableStyleAction
            : tableStyle
        }
        responsive
        noHeader
        selectableRows={selectableRows}
        selectableRowsComponent={SelectableCheckbox}
        striped={striped}
        dense={compactGrid}
        progressPending={loading}
        selectableRowDisabled={rowDisabledCriteria}
        selectableRowSelected={rowPreselectedCriteria}
        noDataComponent={noDataComponent ?? <EmptyView />}
        onSelectedRowsChange={handleSelectedRowsChanged}
        clearSelectedRows={clearSelectedRows}
        onRowClicked={onClickedRow}
        onRowDoubleClicked={onDoubleClickedRow}
        pointerOnHover
        highlightOnHover={false}
        selectableRowsHighlight
      />
    </div>
  );
}

export default KTTable;